import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BookingView, SetBookingListPayload } from 'stores/rematch/models/types/bookings-store';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { FetchBookingsOptions } from './types';
import { v4 as uuid } from 'uuid';

export const useFetchBookings = () => {
  const hasFetchedInitialViews = useRef(false);
  const [fetchedViewIds, setFetchedViewIds] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [hasError, setHasError] = useState(false);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [indeterminateCheck, setIndeterminateCheck] = useState(false);
  const [checkAllIndicator, setCheckAllIndicator] = useState(false);
  const [pageTimestamps, setPageTimestamps] = useState<Record<string, Date>>({});
  const [pageNumbers, setPageNumbers] = useState<Record<string, number>>({});
  const dispatch = useDispatch<IRootDispatch>();

  const bookingListingActiveTab = useSelector((state: IRootState) => state.bookingsStore.bookingListingActiveTab);
  const defaultBookingViews = useSelector((state: IRootState) => state.bookingsStore.defaultBookingViews);
  const displayedBookingListingTabs = useSelector(
    (state: IRootState) => state.bookingsStore.displayedBookingListingTabs,
  );
  const setIsBookingListingLoading = dispatch.bookingsStore.setIsBookingListingLoading;
  const doFetchBookings = dispatch.bookingsStore.doFetchBookings;
  const setSelectedBookings = dispatch.bookingsStore.setSelectedBookings;
  const setBookingList = dispatch.bookingsStore.setBookingList;

  const debugLog = useCallback((message: string, ...args: unknown[]) => {
    // @ts-expect-error -- don't care
    if (process.env.NODE_ENV !== 'production') {
      console.log(message, ...args);
    }
  }, []);

  const resetViews = useCallback(
    (viewIds: string[]) => {
      // biome-ignore lint/complexity/noForEach: <explanation>
      viewIds.forEach((viewId) => {
        setBookingList([{ filterId: viewId, bookings: null }]);
      });

      setPageNumbers((prev) => {
        const next = { ...prev };
        // biome-ignore lint/complexity/noForEach: <explanation>
        viewIds.forEach((viewId) => {
          next[viewId] = 0;
        });
        return next;
      });
      setPageTimestamps((prev) => {
        const next = { ...prev };
        // biome-ignore lint/complexity/noForEach: <explanation>
        viewIds.forEach((viewId) => {
          next[viewId] = new Date();
        });
        return next;
      });
    },
    [setBookingList],
  );

  const fetch = useCallback(
    async ({
      allViews,
      filters,
      initialFetch,
      onFailure,
      onSuccess,
      pageNumber,
      pageTimestamp,
      reset,
      viewId,
      caller,
    }: FetchBookingsOptions) => {
      const id = uuid();
      debugLog('fetchBookings called', id, caller ?? 'no caller');

      if (!initialFetch && !hasFetchedInitialViews.current) {
        debugLog('fetchBookings aborted - must fetch initial views first', id);
        return;
      }

      let cancel = false;
      dispatch.bookingsStore.setIsBookingListingLoading((prev) => {
        if (!initialFetch && prev) {
          debugLog('fetchBookings aborted - already loading', id);
          cancel = true;
          return prev;
        }
        debugLog('fetchBookings loading started', id);
        return true;
      });

      if (cancel) {
        return;
      }

      debugLog('fetchBookings started', id);
      setHasError(false);

      try {
        const allViewsList: BookingView[] = [...displayedBookingListingTabs, ...defaultBookingViews];
        const selectedViewId = viewId ?? bookingListingActiveTab.customViewId;

        if (!selectedViewId) {
          throw new Error('No view selected');
        }

        const selectedViews: BookingView[] = [];

        if (allViews || initialFetch) {
          selectedViews.push(...allViewsList);
        } else {
          const selected = allViewsList.find(({ customViewId }) => customViewId === selectedViewId);
          if (selected) {
            selectedViews.push(selected);
          } else {
            throw new Error(`Selected view ${selectedViewId} not found`);
          }
        }

        const viewIds = selectedViews.map((view) => view?.customViewId);

        if (reset || initialFetch) {
          setShowActionSheet(false);
          setIndeterminateCheck(false);
          setCheckAllIndicator(false);
          resetViews(viewIds);
        }

        const payload = {
          views: selectedViews.map((view) => ({
            id: view.customViewId,
            filters: filters ?? view.filterValue,
            page: pageNumber ?? 1,
            pageSize,
            pageTimestamp: pageTimestamp ?? pageTimestamp?.[view.customViewId] ?? new Date(),
          })),
        };

        const bookings: SetBookingListPayload = await doFetchBookings(payload);

        const bookingsForSelectedView = bookings?.find(({ filterId }) => filterId === selectedViewId);
        if (bookingsForSelectedView) {
          setSelectedBookings((prev) => [
            ...(reset || initialFetch ? [] : prev),
            ...(bookingsForSelectedView.bookings ? bookingsForSelectedView.bookings : []),
          ]);
        }

        hasFetchedInitialViews.current = initialFetch ? true : hasFetchedInitialViews.current;
        setFetchedViewIds((fetchedViewIds) => [
          ...fetchedViewIds.filter((fetchedView) => !viewIds.includes(fetchedView)),
          ...viewIds,
        ]);

        await onSuccess?.(bookings);

        setPageNumbers((prev) => {
          const nextPage = (prev[selectedViewId] ?? 0) + 1;
          return { ...prev, [selectedViewId]: nextPage };
        });
      } catch (e) {
        console.error(e);
        await onFailure?.(e);
        setHasError(true);
      } finally {
        setIsBookingListingLoading(false);
      }
    },
    [
      bookingListingActiveTab.customViewId,
      defaultBookingViews,
      displayedBookingListingTabs,
      doFetchBookings,
      pageSize,
      resetViews,
      setIsBookingListingLoading,
      setSelectedBookings,
      dispatch.bookingsStore.setIsBookingListingLoading,
      debugLog,
    ],
  );

  const onChangeShowActionSheet = useCallback((show: boolean) => {
    setShowActionSheet(show);
  }, []);

  const onChangeIndeterminateCheck = useCallback((indeterminate: boolean) => {
    setIndeterminateCheck(indeterminate);
  }, []);

  const onChangeCheckAllIndicator = useCallback((checked: boolean) => {
    setCheckAllIndicator(checked);
  }, []);

  return {
    checkAllIndicator,
    fetchBookings: fetch,
    fetchedViewIds,
    hasError,
    hasFetchedInitialViews: hasFetchedInitialViews.current,
    indeterminateCheck,
    onChangeCheckAllIndicator,
    onChangeIndeterminateCheck,
    onChangeShowActionSheet,
    pageNumbers,
    pageSize,
    pageTimestamps,
    resetViews,
    showActionSheet,
  };
};
